import { HTMLAttributes, ReactNode, useEffect, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import dayjs from 'lib/dayjs';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useAddVoucherToMap, useVoucherMapValue } from 'hooks/business/conversation';
import { voucher as fetchVoucher } from 'services/api/conversation';
import voucherBackgroundImage from './assets/voucherBackground.png';
import storeVoucherImage from './assets/storeVoucher.jpg';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { formatNumber } from 'utils/numberFormat';
import { toUpperCase } from 'utils/toLowerCase';

const Container = styled.section`
  display: flex;
  flex-direction: column;
  width: 320px;
  height: 137px;
  border: solid 2px #d9e5fd;
  border-radius: 10px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: url(${`"${voucherBackgroundImage}"`});

  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    padding: 10px;
    > div {
      width: 100%;
      color: #fff;
      > h5 {
        margin-bottom: 0;
        line-height: 20px;
        font-size: 12px;
        font-style: italic;
        color: #fff;
        > img {
          margin-right: 5px;
        }
        > span {
          margin: 2px 5px 0 0;
          font-size: 16px;
        }
      }
      > span {
        display: inline-block;
        width: 100%;
        font-size: 13px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    > span {
      flex: 0 0 28px;
      line-height: 28px;
      color: #823102;
    }
  }
  > footer {
    flex: 0 0 30px;
    line-height: 30px;
    padding: 0 10px;
    font-size: 12px;
  }
`;

const StoreIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 10px;
`;

const DiscountAmount = styled.span`
  font-size: 22px;
`;

const voucherTypeIconMap: Record<Conversation.Voucher.Type, ReactNode> = {
  1: <StoreIcon src={storeVoucherImage} alt="store voucher" />,
  2: <InfoCircleOutlined />,
};

export interface VoucherMessageProps extends HTMLAttributes<HTMLDivElement> {
  voucherId: string;
  voucherCode: string;
}

function VoucherMessage(props: VoucherMessageProps) {
  const { voucherId, voucherCode, ...otherProps } = props;

  const { t } = useTranslate();

  const voucherMap = useVoucherMapValue();

  const addVoucherToMap = useAddVoucherToMap();

  const { currentConversation: conversation } = useChatConversationContext();

  const { [voucherId]: voucher } = voucherMap;

  const refsCurrent = {
    addVoucherToMap,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const { channel, storeId } = conversation || {};

  useEffect(() => {
    if (voucher || !channel || !storeId) {
      return;
    }

    fetchVoucher({
      internal_store_id: storeId,
      external_voucher_id: voucherId,
      platform: channel,
    })
      .then((res) => {
        if (!res) {
          throw new Error('Not found');
        }

        const {
          current: { addVoucherToMap },
        } = refs;

        addVoucherToMap(res, storeId);
      })
      .catch(() => {});
  }, [voucher, voucherId, channel, storeId]);

  if (voucher === undefined) {
    return <Container {...otherProps} />;
  }

  const {
    voucherType,
    voucherName,
    platform,
    rewardType,
    unit,
    discountAmount,
    percentage,
    startTime,
    endTime,
    discountType,
    shortDescription,
  } = voucher;

  const upperPlatform = toUpperCase(platform);
  const isJolect = upperPlatform === 'JOLECT';
  const showShortDescription = isJolect && Boolean(shortDescription);
  const hideReward =
    isJolect && (discountType === 'BUY_X_GET_Y' || discountType === 'FREE_SHIPPING');

  const endText =
    endTime.toString() !== '0'
      ? dayjs(Number(endTime) * 1000).format('YYYY.MM.DD HH:mm:ss')
      : t('scene.chatWorkspace.couponTab.label.noEndTime');

  return (
    <Container {...otherProps}>
      <section>
        <div>
          <h5>
            {voucherTypeIconMap[voucherType]}
            {t(`lazadaVoucherType.${voucherType}`)}
          </h5>
          {voucherName ? <span title={voucherName}>{voucherName}</span> : null}
          {showShortDescription ? <span title={shortDescription}>{shortDescription}</span> : null}
        </div>
        {!hideReward && (
          <span>
            {
              {
                1:
                  discountAmount || discountAmount === 0 ? (
                    <span>
                      <DiscountAmount>{discountAmount}</DiscountAmount>
                      {unit}
                    </span>
                  ) : null,
                2: percentage ? (
                  <span>{formatNumber(Number(percentage) / 100, '0.00%')}</span>
                ) : null,
                3: percentage ? (
                  <span>{formatNumber(Number(percentage) / 100, '0.00%')} Coins Cashback</span>
                ) : null,
              }[rewardType]
            }
          </span>
        )}
      </section>
      <footer>{`${dayjs(Number(startTime) * 1000).format('DD/MM/YYYY')}-${endText}`}</footer>
    </Container>
  );
}

export default VoucherMessage;
